import {
	GetPublicAccommodationsResponse,
	GetPublicExpensesResponse,
	GetPublicFilesResponse,
	GetPublicFlightsResponse,
	GetPublicIdeasResponse,
} from '@orbit/api-types'
import { PUBLIC_SECTIONS } from '@components/TripOverview/core/publicSectionRoutes'
import type { TripOverviewSection } from '@components/TripOverview/core/types'
import { publicApi, publicQueryRetry } from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { useQuery } from '@tanstack/react-query'

/**
 * Query options for the five public per-section detail endpoints. URL and cache
 * key both derive from `PUBLIC_SECTIONS`. Each endpoint is gated server-side by
 * the master switch **and** its section flag (ADR 0003), so an OFF section 404s
 * and the deterministic-4xx retry policy drops straight to the unavailable
 * state. All ride the unauthenticated `publicApi`.
 */
const publicSectionQueryOptions = <T>(tripCode: string | undefined, section: TripOverviewSection) => ({
	queryKey: queryKeys.publicSection(section, tripCode),
	queryFn: async (): Promise<T> => {
		if (!tripCode) throw new Error('Trip code is required')
		const response = await publicApi.get<T>(
			`/trips/${tripCode}/${PUBLIC_SECTIONS[section].endpoint}`,
		)
		return response.data
	},
	enabled: !!tripCode,
	retry: publicQueryRetry,
})

export const publicIdeasQueryOptions = (tripCode: string | undefined) =>
	publicSectionQueryOptions<GetPublicIdeasResponse>(tripCode, 'ideas')

export const publicAccommodationsQueryOptions = (tripCode: string | undefined) =>
	publicSectionQueryOptions<GetPublicAccommodationsResponse>(tripCode, 'accommodations')

export const publicFlightsQueryOptions = (tripCode: string | undefined) =>
	publicSectionQueryOptions<GetPublicFlightsResponse>(tripCode, 'flights')

export const publicExpensesQueryOptions = (tripCode: string | undefined) =>
	publicSectionQueryOptions<GetPublicExpensesResponse>(tripCode, 'money')

export const publicFilesQueryOptions = (tripCode: string | undefined) =>
	publicSectionQueryOptions<GetPublicFilesResponse>(tripCode, 'files')

export const usePublicIdeas = (tripCode: string | undefined) =>
	useQuery(publicIdeasQueryOptions(tripCode))

export const usePublicAccommodations = (tripCode: string | undefined) =>
	useQuery(publicAccommodationsQueryOptions(tripCode))

export const usePublicFlights = (tripCode: string | undefined) =>
	useQuery(publicFlightsQueryOptions(tripCode))

export const usePublicExpenses = (tripCode: string | undefined) =>
	useQuery(publicExpensesQueryOptions(tripCode))

export const usePublicFiles = (tripCode: string | undefined) =>
	useQuery(publicFilesQueryOptions(tripCode))
